import { useState } from "react"
import { Bookmark } from "lucide-react"
import { toast } from "sonner"
import { useCurrentUser } from "../../../hooks/useCurrentUser"
import { useRunWhenReady } from "../../../hooks/useRunWhenReady"
import { getCsrfToken } from "../utils"
import { SignInModal } from "./SignInModal"
import { CollectionsDialog } from "./CollectionsDialog"
import type { BookmarkData } from "../types"

interface BookmarkButtonProps {
  bookmark: BookmarkData
  coffeeShopSlug: string
  coffeeShopId: number
  appleAuthUrl: string
  googleAuthUrl: string
}

export function BookmarkButton({
  bookmark: serverBookmark,
  coffeeShopSlug,
  coffeeShopId,
  appleAuthUrl,
  googleAuthUrl,
}: BookmarkButtonProps) {
  const { data: meData, isLoading: meLoading } = useCurrentUser()
  const currentUser = meData?.current_user ?? null

  // Derive initial bookmark state from /me data
  const meBookmarkId = meData?.bookmarks?.[String(coffeeShopId)] ?? null
  const meIsBookmarked = meBookmarkId !== null

  const [isBookmarked, setIsBookmarked] = useState<boolean | null>(null)
  const [bookmarkId, setBookmarkId] = useState<number | null>(null)
  const [bookmarkCount, setBookmarkCount] = useState(
    serverBookmark.bookmark_count
  )
  const [destroyUrl, setDestroyUrl] = useState<string | null>(null)
  const [editUrl, setEditUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showSignIn, setShowSignIn] = useState(false)
  const [showCollections, setShowCollections] = useState(false)

  // Use local state if user has interacted, otherwise use /me data
  const effectiveIsBookmarked = isBookmarked ?? meIsBookmarked
  const effectiveBookmarkId = bookmarkId ?? meBookmarkId
  const effectiveDestroyUrl =
    destroyUrl ?? (meBookmarkId ? `/bookmarks/${meBookmarkId}` : null)
  const effectiveEditUrl =
    editUrl ?? (meBookmarkId ? `/bookmarks/${meBookmarkId}/edit` : null)

  function handleRemoveBookmark(newBookmarkCount: number) {
    setIsBookmarked(false)
    setBookmarkCount(newBookmarkCount)
    setBookmarkId(null)
    setDestroyUrl(null)
    setEditUrl(null)
  }

  async function performClick() {
    if (!currentUser) {
      setShowSignIn(true)
      return
    }

    if (isLoading) return

    if (effectiveIsBookmarked) {
      // Already bookmarked — open collections dialog
      setShowCollections(true)
      return
    }

    // Not bookmarked — create bookmark
    const previousState = {
      isBookmarked: effectiveIsBookmarked,
      bookmarkId: effectiveBookmarkId,
      bookmarkCount,
      destroyUrl,
      editUrl,
    }

    setIsBookmarked(true)
    setBookmarkCount((c) => c + 1)
    setIsLoading(true)

    try {
      const res = await fetch(serverBookmark.create_url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ coffee_shop_id: coffeeShopSlug }),
      })
      if (!res.ok) throw new Error("Failed")
      const data = await res.json()
      setBookmarkId(data.bookmark_id)
      setBookmarkCount(data.bookmark_count)
      setDestroyUrl(data.destroy_url)
      setEditUrl(data.edit_url)

      toast.success("Coffee shop has been bookmarked.", {
        action: {
          label: "Add to Collections",
          onClick: () => setShowCollections(true),
        },
        actionButtonStyle: {
          backgroundColor: "rgb(22 163 74)",
          color: "white",
        },
      })
    } catch {
      setIsBookmarked(previousState.isBookmarked)
      setBookmarkCount(previousState.bookmarkCount)
    } finally {
      setIsLoading(false)
    }
  }

  const { run: handleClick, pending } = useRunWhenReady(
    !meLoading,
    performClick
  )

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        aria-busy={meLoading || pending}
        className="w-full rounded-lg bg-card px-3.5 py-3.5 text-md font-semibold text-foreground-secondary shadow-sm ring-1 ring-inset ring-border-strong hover:bg-surface-subtle hover:ring-foreground-faint flex items-center space-x-3 disabled:opacity-50 transition-all duration-150 hover:shadow-md active:scale-[0.98]"
      >
        <Bookmark
          className={`h-5 w-5 shrink-0 ${effectiveIsBookmarked ? "fill-current" : ""}`}
        />
        <span className="flex-1 text-center">
          {bookmarkCount > 0 ? `Bookmark (${bookmarkCount})` : "Bookmark"}
        </span>
      </button>

      <SignInModal
        open={showSignIn}
        onOpenChange={setShowSignIn}
        title="Sign In Required"
        description="Please sign in to bookmark this coffee shop."
        appleAuthUrl={appleAuthUrl}
        googleAuthUrl={googleAuthUrl}
      />

      {effectiveEditUrl && effectiveBookmarkId && effectiveDestroyUrl && (
        <CollectionsDialog
          open={showCollections}
          onOpenChange={setShowCollections}
          editUrl={effectiveEditUrl}
          destroyUrl={effectiveDestroyUrl}
          collectionsUrl={serverBookmark.collections_url}
          bookmarkId={effectiveBookmarkId}
          onRemoveBookmark={handleRemoveBookmark}
        />
      )}
    </>
  )
}
